/**
 * Criteria + values on an item card — the presentation half.
 *
 * Every prioritization tool asks people to judge items. They can only judge well
 * if the criteria the session agreed on (S3) and the values recorded against them
 * (S4) are visible on the card in front of them — otherwise the tool asks for a
 * decision while withholding the inputs to it.
 *
 * Pairwise ranking was the only tool of eighteen that showed them, and its block
 * had already been copy-pasted twice with the two copies drifting. This is the one
 * shared version; the data half lives in useItemCriteriaValues.ts.
 *
 * Two variants, because that drift was not accidental — the standalone tool pages
 * are light-themed and the live session canvas is dark:
 *   'page' — light surfaces (tool pages)
 *   'live' — dark session canvas (liveExperience/liveTools)
 */

import type { CriterionValueDisplay } from './useItemCriteriaValues';

type Variant = 'page' | 'live';

const STYLES: Record<
  Variant,
  { label: string; dotOn: string; dotOff: string; readout: (has: boolean) => string }
> = {
  page: {
    label: 'block text-[0.65rem] font-semibold uppercase tracking-wide text-[var(--text-ter)] mb-0.5',
    dotOn: 'inline-block w-2 h-2 rounded-full border bg-[var(--slate)] border-[var(--slate)]',
    dotOff: 'inline-block w-2 h-2 rounded-full border bg-transparent border-[var(--rule)]',
    readout: (has) =>
      `ml-1.5 font-mono text-[0.6rem] tabular-nums ${has ? 'text-[var(--text-ter)]' : 'text-[var(--text-ter)]'}`,
  },
  live: {
    label: 'block font-heading text-[0.48rem] tracking-[0.08em] uppercase text-[#3A4A60] mb-0.5',
    dotOn:
      'inline-block w-[7px] h-[7px] rounded-full transition-colors bg-slate-blue-400 shadow-[0_0_3px_rgba(100,130,220,0.4)]',
    dotOff:
      'inline-block w-[7px] h-[7px] rounded-full transition-colors bg-transparent border border-[#2A3A54]',
    readout: (has) =>
      `ml-1.5 font-mono text-[0.55rem] tabular-nums ${has ? 'text-[#6B7A96]' : 'text-[#2A3A54]'}`,
  },
};

export function CriterionScale({
  name,
  value,
  maxValue,
  variant = 'page',
}: CriterionValueDisplay & { variant?: Variant }) {
  const s = STYLES[variant];
  const hasValue = value > 0;
  return (
    <div>
      <span className={s.label}>{name}</span>
      <div className="flex items-center gap-[3px]">
        {Array.from({ length: maxValue }, (_, i) => (
          <span key={i} className={i < value ? s.dotOn : s.dotOff} />
        ))}
        {/* An unscored criterion reads "—", never "0/10": zero is a judgement
            somebody made, absent is not. */}
        <span className={s.readout(hasValue)}>{hasValue ? `${value}/${maxValue}` : '—'}</span>
      </div>
    </div>
  );
}

export interface CriteriaScalesProps {
  values?: CriterionValueDisplay[];
  variant?: Variant;
  className?: string;
}

/** The whole block for one card. Renders nothing when the session has no criteria. */
export function CriteriaScales({ values, variant = 'page', className }: CriteriaScalesProps) {
  if (!values || values.length === 0) return null;
  return (
    <div className={className ?? 'mt-3 space-y-1.5 text-left'}>
      {values.map((v) => (
        <CriterionScale key={v.id} {...v} variant={variant} />
      ))}
    </div>
  );
}
